import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Button, Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ResponsiveTable from './ResponsiveTable';
import { BsPencil, BsTrash } from 'react-icons/bs';
import messageCategoryService from '../services/messageCategoryService';
import userService from '../services/userService';
import Select from 'react-select';

const MessageCategories = () => {
    const { user } = useContext(AuthContext);
    const [categories, setCategories] = useState([]);
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        defaultRecipientIds: []
    });
    const [selectedUsers, setSelectedUsers] = useState([]);

    useEffect(() => {
        loadCategories();
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            const data = await userService.getAllUsers();
            const userOptions = data.map(user => ({
                value: user.id,
                label: `${user.username} (${user.email})`
            }));
            setUsers(userOptions);
        } catch (error) {
            console.error('Error loading users:', error);
            toast.error('Failed to load users');
        }
    };

    const loadCategories = async () => {
        try {
            const data = await messageCategoryService.getAll();
            setCategories(data);
        } catch (error) {
            console.error('Error loading categories:', error);
            toast.error('Failed to load message categories');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const categoryData = {
                ...formData,
                id: selectedCategory?.id,
                defaultRecipientIds: selectedUsers.map(user => user.value)
            };

            if (selectedCategory) {
                await messageCategoryService.update(selectedCategory.id, categoryData);
            } else {
                await messageCategoryService.create(categoryData);
            }

            toast.success(`Category ${selectedCategory ? 'updated' : 'created'} successfully`);
            setShowModal(false);
            setFormData({ name: '', description: '', defaultRecipientIds: [] });
            setSelectedUsers([]);
            setSelectedCategory(null);
            loadCategories();
        } catch (error) {
            console.error('Error saving category:', error);
            toast.error('Failed to save message category');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this category?')) {
            try {
                await messageCategoryService.delete(id);
                toast.success('Category deleted successfully');
                loadCategories();
            } catch (error) {
                console.error('Error deleting category:', error);
                toast.error('Failed to delete category');
            }
        }
    };

    const handleEdit = (category) => {
        setSelectedCategory(category);
        const selectedUserOptions = category.defaultRecipientIds.map(id => {
            const user = users.find(u => u.value === id);
            return user || null;
        }).filter(u => u !== null);
        
        setFormData({
            name: category.name,
            description: category.description,
            defaultRecipientIds: category.defaultRecipientIds
        });
        setSelectedUsers(selectedUserOptions);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedCategory(null);
        setFormData({ name: '', description: '', defaultRecipientIds: [] });
        setSelectedUsers([]);
    };

    const columns = [
        { key: 'name', label: 'Name' },
        { key: 'description', label: 'Description' },
        { 
            key: 'defaultRecipientIds', 
            label: 'Default Recipients',
            render: (value) => {
                if (!Array.isArray(value)) return '';
                return value.map(id => {
                    const user = users.find(u => u.value === id);
                    return user ? user.label.split(' (')[0] : '';
                }).filter(Boolean).join(', ');
            }
        }
    ];

    const actions = [
        {
            icon: <BsPencil />,
            variant: "outline-primary",
            onClick: handleEdit,
            label: "Edit"
        },
        {
            icon: <BsTrash />,
            variant: "outline-danger",
            onClick: (item) => handleDelete(item.id),
            label: "Delete"
        }
    ];

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-end mb-4">
                <Button variant="primary" onClick={() => setShowModal(true)}>
                    Create Category
                </Button>
            </div>

            <ResponsiveTable 
                data={categories}
                columns={columns}
                actions={actions}
            />

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedCategory ? 'Edit Category' : 'Create Category'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">Default Recipients</Form.Label>
                                <Button 
                                    variant="link" 
                                    size="sm" 
                                    onClick={() => setSelectedUsers(users)}
                                    className="p-0"
                                >
                                    Select All
                                </Button>
                            </div>
                            <Select
                                isMulti
                                options={users}
                                value={selectedUsers}
                                onChange={setSelectedUsers}
                                placeholder="Select default recipients..."
                            />
                        </Form.Group>
                        <Button variant="primary" type="submit">
                            {selectedCategory ? 'Update' : 'Create'}
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default MessageCategories; 